// @ts-ignore - allow import with query string
import { formatMoney } from './util.js?v=25';

/**
 * @typedef {{ id: number | string, available: boolean, option1: string | null, option2: string | null, option3: string | null, options: string[], price: number, [key: string]: unknown }} ShopifyVariant
 */

/**
 * Executes a callback after the next paint to improve performance.
 * @param {() => void} cb
 */
const afterPaint = (cb) => requestAnimationFrame(() => setTimeout(cb, 0));

if (!customElements.get('variant-radios')) {
  customElements.define(
    'variant-radios',
    class VariantRadios extends HTMLElement {
      constructor() {
        super();

        this.elements = this.getElements();
        this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.variantData = this.getVariantData();

        this.availabilityByFirstOption = this.buildAvailabilityByFirstOption(this.variantData);

        document.addEventListener('fulfillmentLocationAvailabilityLoaded', () => {
          const updatedAvailability = /** @type {ShopifyVariant[]} */ (
            window.theme.fulfillmentLocationAvailability || []
          );

          this.variantData = this.variantData.map((/** @type {ShopifyVariant} */ variant) => {
            const match = updatedAvailability.find((v) => String(v.id) === String(variant.id));
            return match ? { ...variant, available: match.available } : variant;
          });

          this.availabilityByFirstOption = this.buildAvailabilityByFirstOption(this.variantData);
          this.onVariantChange();
        });

        if (this.shouldUpdateData) {
          this.addEventListener('change', this.onVariantChange.bind(this));
          this.updateSpeculatorColor();
        }

        if (this.elements.availableSizes && this.elements.colorInputs) {
          if (window.matchMedia?.('(hover: hover)').matches) {
            this.elements.colorInputs.forEach((input) => {
              const label = input.closest('label');
              if (!label) return;
              label.addEventListener('mouseover', this.getAvailableSizes.bind(this, input));
              label.addEventListener('mouseout', this.hideAvailableSizes.bind(this));
            });
          }
        }
      }

      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.removeErrorMessage();

        if (!this.currentVariant) {
          this.toggleAddButton(false);
          return;
        }

        this.updateURL();
        this.updateVariantInput();
        this.updateSelectedVariantTexts();

        this.toggleAddButton(!this.currentVariant.available);

        afterPaint(() => {
          this.updateVariantStatuses();
          this.updateMedia();
          this.updatePrice();
          this.updateSpeculatorColor();

          document.dispatchEvent(
            new CustomEvent('variantUpdated', {
              detail: { variant: this.currentVariant },
              bubbles: true,
            }),
          );
        });
      }

      updateOptions() {
        this.options = this.fieldsets.map((fs) => {
          const checked = fs.querySelector('input:checked');
          return checked ? checked.value : undefined;
        });
      }

      updateMasterId() {
        if (this.variantData.length > 1) {
          this.currentVariant = this.variantData.find((variant) => {
            return !variant.options.map((option, index) => this.options[index] === option).includes(false);
          });
          return;
        }

        this.currentVariant = this.variantData[0];
      }

      updateMedia() {
        if (!this.currentVariant) return;

        /** @type {NodeListOf<HTMLElement>} */
        const productMediaGalleryItems = document.querySelectorAll('[js-variant-radios-media-gallery-item]');
        const visibleGalleryItems = Array.from(productMediaGalleryItems).filter(
          (item) => item.getAttribute('aria-hidden') === 'false',
        );
        const currentVariantColor = this.currentVariant.option2 || this.currentVariant.option1;
        const previousVariantColor = visibleGalleryItems[0]?.dataset?.color;

        if (currentVariantColor === previousVariantColor) return;

        let newColorImage;

        productMediaGalleryItems.forEach((item) => {
          if (item.dataset.color === currentVariantColor) {
            item.setAttribute('aria-hidden', 'false');
            /** @type {HTMLImageElement | null} */
            const img = item.querySelector('img');
            if (img) {
              img.loading = 'eager';
              this.updateFeaturedMedia(img);
              newColorImage = img;
            }
          }
        });

        if (!newColorImage) return;

        if (newColorImage.complete) {
          this.hideVisibleMedia(visibleGalleryItems);
        } else {
          newColorImage.addEventListener('load', () => this.hideVisibleMedia(visibleGalleryItems));
        }
      }

      updatePrice() {
        this.elements.price.forEach((price) => {
          price.textContent = formatMoney(
            this.currentVariant.price,
            `${window.theme.moneyFormat.charAt(0)}{{amount_no_decimals}}`,
          );
        });
      }

      updateSpeculatorColor() {
        if (!this.selectedColor || !window.Speculator?.set3DModelConfig) return;
        window.Speculator.set3DModelConfig(this.selectedColor.dataset.colorSpeculator);
      }

      /** @param {Array<HTMLElement>} images */
      hideVisibleMedia(images) {
        if (!images) return;
        images.forEach((item) => item.setAttribute('aria-hidden', 'true'));
        this.dispatchEvent(new Event('productMediaGalleryUpdate', { bubbles: true }));
      }

      /** @param {HTMLImageElement} image */
      updateFeaturedMedia(image) {
        /** @type {HTMLImageElement | null} */
        const productFeaturedMedia = document.querySelector('[js-variant-radios-featured-media] img');
        if (!productFeaturedMedia) return;
        productFeaturedMedia.src = image.src;
        productFeaturedMedia.alt = image.alt;
        productFeaturedMedia.srcset = image.srcset;
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}`);
        productForms.forEach((productForm) => {
          /** @type {HTMLInputElement | null} */
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateVariantStatuses() {
        const inputWrappers = this.querySelectorAll('.product-option-selector__options');
        if (!inputWrappers.length) return;

        const firstChecked = this.querySelector('fieldset input:checked');
        const opt1Value = firstChecked ? firstChecked.value : null;

        inputWrappers.forEach((optionWrapper, index) => {
          if (index === 0) return;
          const optionInputs = optionWrapper.querySelectorAll('input[type="radio"]');
          const prevChecked = inputWrappers[index - 1].querySelector('input:checked');
          const prevValue = prevChecked ? prevChecked.value : null;

          let availableValues = [];
          if (index === 1 && opt1Value) {
            const set = this.availabilityByFirstOption.get(opt1Value);
            if (set) availableValues = Array.from(set);
          } else {
            // fallback: om fler dimensioner behövs senare
            availableValues = [];
          }

          this.setInputAvailability(optionInputs, availableValues);
        });
      }

      /**
       * @param {Array<any>} variants
       */
      buildAvailabilityByFirstOption(variants) {
        const availabilityMap = new Map();

        variants?.forEach((variant) => {
          if (!variant.available) return;

          const option1 = variant.option1;
          const option2 = variant.option2;

          if (!availabilityMap.has(option1)) {
            availabilityMap.set(option1, new Set());
          }
          if (option2) {
            availabilityMap.get(option1).add(option2);
          }
        });

        return availabilityMap;
      }

      updateSelectedVariantTexts() {
        const variantSizes = document.querySelectorAll('[js-variant-size]');
        const variantColors = document.querySelectorAll('[js-variant-color]');
        const size = this.currentVariant?.option1.replace(/.*\|/, '');
        const color = this.currentVariant?.option2;

        variantSizes.forEach((el) => (el.textContent = size));
        variantColors.forEach((el) => (el.textContent = color));
      }

      /** @param {Array<HTMLInputElement>} elementList @param {Array<string>} availableValuesList */
      setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((el) => {
          const value = el?.getAttribute('value');
          if (!value) return;
          const available = availableValuesList.includes(value);
          el.classList.toggle('disabled', !available);
        });
      }

      removeErrorMessage() {
        const section = this.closest('section');
        if (!section) return;
        section.querySelector('product-form');
      }

      toggleAddButton(disable = true) {
        if (!this.elements.backInStock.length) return;
        if (disable) {
          this.disableButtons(this.elements.addToCart);
          this.enableButtons(this.elements.backInStock);
        } else {
          this.disableButtons(this.elements.backInStock);
          this.enableButtons(this.elements.addToCart);
        }
      }

      /** @returns {ShopifyVariant[]} */
      getVariantData() {
        if (this.variantData) return this.variantData;

        /** @type {HTMLScriptElement | null} */
        const el = this.querySelector('[type="application/json"]');
        if (!el?.textContent) return [];

        this.variantData = JSON.parse(el.textContent);
        return this.variantData;
      }

      getAvailableSizes(input) {
        const colorVariants = this.variantData.filter((v) => input?.value === v.option2);
        /** @type {NodeListOf<HTMLElement>} */
        const sizeInputs = this.querySelectorAll('input[data-variant-type="size"]');
        if (!this.elements.availableSizes || !colorVariants) return;

        const availableOptionInputsValue = colorVariants
          .filter((v) => v.available && v.option2 === input?.value)
          .map((v) => v.option1);

        if (!availableOptionInputsValue) return;

        let sizesLabel = '';
        sizeInputs.forEach((el) => {
          const value = el.getAttribute('value');
          const shortValue = el.dataset.value;
          const available = availableOptionInputsValue.includes(value);
          sizesLabel += available ? `<span>${shortValue}</span>` : `<s class="text-color-grey-5">${shortValue}</s>`;
        });

        this.elements.availableSizes.innerHTML = sizesLabel;
        this.elements.availableSizes?.closest('div')?.removeAttribute('aria-hidden');
      }

      hideAvailableSizes() {
        this.elements.availableSizes?.closest('div')?.setAttribute('aria-hidden', 'true');
      }

      /** @param {Array<HTMLButtonElement>} buttons */
      enableButtons(buttons) {
        buttons.forEach((b) => {
          b.removeAttribute('disabled');
          b.removeAttribute('aria-hidden');
        });
      }

      /** @param {Array<HTMLButtonElement>} buttons */
      disableButtons(buttons) {
        buttons.forEach((b) => {
          b.setAttribute('disabled', 'true');
          b.setAttribute('aria-hidden', 'true');
        });
      }

      get shouldUpdateData() {
        return this.dataset.updateData === 'true';
      }

      getElements() {
        return {
          addToCart: document.querySelectorAll('.product-form__button--add-to-cart'),
          backInStock: document.querySelectorAll('.product-form__button--back-in-stock'),
          /** @type {NodeListOf<HTMLInputElement> | null} */
          colorInputs: document.querySelectorAll('[data-variant-type="colour"]'),
          availableSizes: document.querySelector('[js-variant-radios-available-sizes]'),
          price: document.querySelectorAll('[js-variant-radios-price]'),
        };
      }

      get selectedColor() {
        if (!this.elements.colorInputs) return null;
        return Array.from(this.elements.colorInputs).find((input) => input.checked) || null;
      }
    },
  );
}
